/**
 * Canonical Wagamour V2 surface tokens.
 *
 * Source of truth for material rendering: blur, edge highlight, warm amber body,
 * layered depth. Home `homeV2Glass` values are referenced — not duplicated — for
 * story and utility tiers. Modal is a denser extension of the same family.
 *
 * Future migrations: Play, Parks, Picks, modals → WagamourV2Surface.
 * Home remains on HomeV2GlassCard until an explicit migration pass.
 */

import { homeV2Glass, type HomeV2GlassVariant } from '@/lib/home/homeV2Theme'

export type WagamourV2SurfaceVariant = 'story' | 'utility' | 'modal'

export type WagamourV2SurfaceToken = {
  background: string
  border: string
  shadow: string
  radius: string
  /** Optional top ambient bloom (story + modal). */
  ambientBloom?: string
}

const glassEdgeHighlight = 'inset 0 1px 0 rgba(255, 235, 200, 0.42)'
const warmInnerGlow =
  'inset 0 1px 0 rgba(255, 220, 165, 0.36), inset 0 0 64px rgba(255, 160, 70, 0.14)'
const modalOuterShadow = '0 20px 56px rgba(0, 0, 0, 0.36), 0 6px 18px rgba(0, 0, 0, 0.20)'

/** Inset body glow for modal clip host — no top hairline (hairline breaks rounded corners). */
export const wagamourV2ModalShellBodyGlow = warmInnerGlow

/** Inset edge + body glow — for standalone WagamourV2Surface cards, NOT modal clip hosts. */
export const wagamourV2ModalShellInsetShadow = `${glassEdgeHighlight}, ${warmInnerGlow}`

/** Outer drop shadow — render on WagamourV2Modal outer wrapper only (outside overflow-hidden). */
export const wagamourV2ModalShellOuterShadow = modalOuterShadow

/** Shared backdrop blur strength — matches HomeV2GlassCard. */
export const wagamourV2SurfaceBlur = homeV2Glass.blur

/**
 * Story — hero cards, match cards, recommendation surfaces.
 * Maps to approved Home `hero` glass (blur + bloom + edge highlight).
 */
const storyToken: WagamourV2SurfaceToken = {
  background: homeV2Glass.hero.background,
  border: homeV2Glass.hero.border,
  shadow: homeV2Glass.hero.shadow,
  radius: homeV2Glass.hero.radius,
  ambientBloom: homeV2Glass.hero.ambientBloom,
}

/**
 * Utility — chips, filter rows, location bars, secondary panels.
 * Lighter nested glass from Home.
 */
const utilityToken: WagamourV2SurfaceToken = {
  background: homeV2Glass.nested.background,
  border: homeV2Glass.nested.border,
  shadow: homeV2Glass.nested.shadow,
  radius: homeV2Glass.nested.radius,
}

/**
 * Modal — denser amber frost for dialogs and sheets.
 * Same material family; higher opacity and stronger outer shadow.
 */
const modalToken: WagamourV2SurfaceToken = {
  background:
    'linear-gradient(165deg, rgba(52, 28, 12, 0.54) 0%, rgba(92, 50, 18, 0.40) 48%, rgba(118, 66, 24, 0.30) 100%)',
  border: 'rgba(255, 228, 185, 0.34)',
  shadow: `${glassEdgeHighlight}, ${warmInnerGlow}, ${modalOuterShadow}`,
  radius: 'rounded-[28px]',
  ambientBloom:
    'radial-gradient(ellipse 90% 55% at 50% 0%, rgba(255, 175, 85, 0.22) 0%, transparent 68%)',
}

export const wagamourV2Surface: Record<WagamourV2SurfaceVariant, WagamourV2SurfaceToken> = {
  story: storyToken,
  utility: utilityToken,
  modal: modalToken,
}

/** Map legacy Home glass variant names → canonical V2 surface tier (for future refactors). */
export const homeGlassToV2Surface = {
  hero: 'story',
  surface: 'story',
  nested: 'utility',
  whisper: 'utility',
  pill: 'utility',
} as const satisfies Record<HomeV2GlassVariant, WagamourV2SurfaceVariant>
